import React, { useState, useEffect } from 'react';
import { BarChart3, Clock, TrendingUp, MessageSquare, Phone, Users, Calendar, ArrowUp, ArrowDown, Loader2, Download, Brain, Target, GitCompare, Award, Zap, AlertTriangle, ThumbsUp, ThumbsDown } from 'lucide-react';
import { analyticsAPI } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import ExportPDFModal from './ExportPDFModal';

const VoiceAnalyticsDashboard = ({ clientId }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [talkTimeData, setTalkTimeData] = useState(null);
  const [keywordData, setKeywordData] = useState(null);
  const [outcomeData, setOutcomeData] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [conversationIntelligence, setConversationIntelligence] = useState(null);
  const [performanceInsights, setPerformanceInsights] = useState(null);
  const [periodComparison, setPeriodComparison] = useState(null);
  const [agentComparison, setAgentComparison] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    if (clientId) {
      fetchAnalytics();
    }
  }, [clientId, days]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      
      const [talkTime, keywords, outcomes, trends, intelligence, insights, comparison, agents] = await Promise.all([
        analyticsAPI.voiceAnalytics.getTalkTime(clientId, days),
        analyticsAPI.voiceAnalytics.getKeywords(clientId, days),
        analyticsAPI.voiceAnalytics.getOutcomes(clientId, days),
        analyticsAPI.voiceAnalytics.getDailyTrends(clientId, days),
        analyticsAPI.voiceAnalytics.getConversationIntelligence(clientId, days).catch(() => ({ data: null })),
        analyticsAPI.voiceAnalytics.getPerformanceInsights(clientId, days).catch(() => ({ data: null })),
        analyticsAPI.voiceAnalytics.getPeriodComparison(clientId, 7, 7).catch(() => ({ data: null })),
        analyticsAPI.voiceAnalytics.getAgentComparison(clientId, days).catch(() => ({ data: null }))
      ]);
      
      setTalkTimeData(talkTime.data);
      setKeywordData(keywords.data);
      setOutcomeData(outcomes.data);
      setTrendsData(trends.data);
      
      // Only set advanced analytics if they have actual data (not error or empty)
      const intelligenceData = intelligence.data;
      setConversationIntelligence(
        intelligenceData?.success && !intelligenceData?.message?.includes('No calls') 
          ? intelligenceData 
          : null
      );
      
      const insightsData = insights.data;
      setPerformanceInsights(
        insightsData?.success && !insightsData?.message?.includes('No calls')
          ? insightsData
          : null
      );
      
      const comparisonData = comparison.data;
      setPeriodComparison(comparisonData?.success ? comparisonData : null);
      
      const agentsData = agents.data;
      setAgentComparison(agentsData?.success ? agentsData : null);
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: t('voiceAnalytics.tabOverview'), icon: BarChart3 },
    { id: 'talk-time', label: t('voiceAnalytics.tabTalkTime'), icon: Clock },
    { id: 'keywords', label: t('voiceAnalytics.tabKeywords'), icon: MessageSquare },
    { id: 'outcomes', label: t('voiceAnalytics.tabOutcomes'), icon: TrendingUp },
    { id: 'intelligence', label: t('voiceAnalytics.tabAiInsights'), icon: Brain },
    { id: 'performance', label: t('voiceAnalytics.tabPerformance'), icon: Target },
    { id: 'comparison', label: t('voiceAnalytics.tabCompare'), icon: GitCompare }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">{t('voiceAnalytics.title')}</h2>
          <p className="text-gray-400">{t('voiceAnalytics.subtitle')}</p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
          >
            <option value={7}>{t('analytics.last7Days')}</option>
            <option value={14}>{t('analytics.last14Days')}</option>
            <option value={30}>{t('analytics.last30Days')}</option>
            <option value={60}>{t('analytics.lastDays', { days: 60 })}</option>
            <option value={90}>{t('analytics.last90Days')}</option>
          </select>
          <button
            onClick={() => setShowExportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
            data-testid="export-pdf-button"
          >
            <Download className="w-4 h-4" />
            {t('voiceAnalytics.exportPdf')}
          </button>
        </div>
      </div>

      {/* Export PDF Modal */}
      <ExportPDFModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        talkTimeData={talkTimeData}
        keywordData={keywordData}
        outcomeData={outcomeData}
        trendsData={trendsData}
        days={days}
      />

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700 pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-purple-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-800'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-4 gap-4">
            <MetricCard
              title={t('voiceAnalytics.totalCalls')}
              value={talkTimeData?.total_calls || 0}
              icon={Phone}
              color="blue"
            />
            <MetricCard
              title={t('voiceAnalytics.talkTime')}
              value={`${talkTimeData?.metrics?.total_talk_time_hours || 0}h`}
              icon={Clock}
              color="green"
            />
            <MetricCard
              title={t('voiceAnalytics.answerRate')}
              value={`${outcomeData?.conversion_funnel?.answered_rate || 0}%`}
              icon={TrendingUp}
              color="purple"
            />
            <MetricCard
              title={t('voiceAnalytics.transferRate')}
              value={`${outcomeData?.transfer_analysis?.transfer_rate_percent || 0}%`}
              icon={Users}
              color="amber"
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-2 gap-6">
            {/* Hourly Distribution */}
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">{t('voiceAnalytics.hourlyDistribution')}</h3>
              <div className="h-48 flex items-end gap-1">
                {talkTimeData?.hourly_distribution && 
                  Object.entries(talkTimeData.hourly_distribution).map(([hour, count]) => {
                    const maxCount = Math.max(...Object.values(talkTimeData.hourly_distribution));
                    const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                    return (
                      <div key={hour} className="flex-1 flex flex-col items-center">
                        <div 
                          className="w-full bg-purple-500 rounded-t"
                          style={{ height: `${height}%`, minHeight: count > 0 ? '4px' : '0' }}
                        />
                        <span className="text-xs text-gray-500 mt-1">
                          {hour.split(':')[0]}
                        </span>
                      </div>
                    );
                  })
                }
              </div>
            </div>

            {/* Duration Distribution */}
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">{t('voiceAnalytics.durationDistribution')}</h3>
              <div className="space-y-3">
                {talkTimeData?.duration_distribution &&
                  Object.entries(talkTimeData.duration_distribution).map(([bucket, count]) => {
                    const total = Object.values(talkTimeData.duration_distribution).reduce((a, b) => a + b, 0);
                    const percent = total > 0 ? (count / total) * 100 : 0;
                    return (
                      <div key={bucket} className="flex items-center gap-3">
                        <span className="text-sm text-gray-400 w-16">{bucket}</span>
                        <div className="flex-1 bg-gray-700 rounded-full h-3">
                          <div 
                            className="bg-blue-500 rounded-full h-3"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        <span className="text-sm text-white w-12 text-right">{count}</span>
                      </div>
                    );
                  })
                }
              </div>
            </div>
          </div>

          {/* Daily Trends Chart */}
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">{t('voiceAnalytics.dailyTrends')}</h3>
            <div className="h-48 flex items-end gap-2">
              {trendsData?.daily_trends?.slice(-14).map((day, i) => {
                const maxCalls = Math.max(...trendsData.daily_trends.map(d => d.total_calls));
                const height = maxCalls > 0 ? (day.total_calls / maxCalls) * 100 : 0;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center">
                    <div 
                      className="w-full bg-green-500 rounded-t"
                      style={{ height: `${height}%`, minHeight: day.total_calls > 0 ? '4px' : '0' }}
                    />
                    <span className="text-xs text-gray-500 mt-1 rotate-45 origin-left">
                      {day.date.slice(5)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Talk Time Tab */}
      {activeTab === 'talk-time' && (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <MetricCard
              title={t('voiceAnalytics.talkTime')}
              value={`${talkTimeData?.metrics?.total_talk_time_hours || 0} hours`}
              subtitle={`${talkTimeData?.metrics?.total_talk_time_minutes || 0} minutes`}
              icon={Clock}
              color="green"
            />
            <MetricCard
              title={t('voiceAnalytics.avgDuration')}
              value={`${Math.round(talkTimeData?.metrics?.average_duration_seconds || 0)}s`}
              subtitle={`${Math.round((talkTimeData?.metrics?.average_duration_seconds || 0) / 60)} min avg`}
              icon={BarChart3}
              color="blue"
            />
            <MetricCard
              title={t('voiceAnalytics.peakHour')}
              value={talkTimeData?.peak_analysis?.peak_hour || 'N/A'}
              subtitle={`${talkTimeData?.peak_analysis?.peak_hour_calls || 0} ${t('voiceAnalytics.callsAtPeak')}`}
              icon={TrendingUp}
              color="purple"
            />
          </div>

          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">{t('voiceAnalytics.peakAnalysis')}</h3>
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-white">{talkTimeData?.metrics?.min_duration_seconds || 0}s</p>
                <p className="text-gray-400">{t('voiceAnalytics.minDuration')}</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-white">{Math.round(talkTimeData?.metrics?.average_duration_seconds || 0)}s</p>
                <p className="text-gray-400">{t('voiceAnalytics.avgDuration')}</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-white">{talkTimeData?.metrics?.max_duration_seconds || 0}s</p>
                <p className="text-gray-400">{t('voiceAnalytics.maxDuration')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Keywords Tab */}
      {activeTab === 'keywords' && (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <MetricCard
              title="Transfer Requests"
              value={keywordData?.phrase_analysis?.transfer_requests || 0}
              icon={Users}
              color="amber"
            />
            <MetricCard
              title="Positive Sentiment"
              value={keywordData?.phrase_analysis?.positive_sentiment || 0}
              icon={ArrowUp}
              color="green"
            />
            <MetricCard
              title="Negative Sentiment"
              value={keywordData?.phrase_analysis?.negative_sentiment || 0}
              icon={ArrowDown}
              color="red"
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Customer Keywords */}
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Top Customer Keywords</h3>
              <div className="space-y-2">
                {keywordData?.top_keywords?.from_customers &&
                  Object.entries(keywordData.top_keywords.from_customers).slice(0, 10).map(([word, count]) => (
                    <div key={word} className="flex justify-between items-center">
                      <span className="text-gray-300">{word}</span>
                      <span className="text-purple-400 font-medium">{count}</span>
                    </div>
                  ))
                }
              </div>
            </div>

            {/* Agent Keywords */}
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Top Agent Keywords</h3>
              <div className="space-y-2">
                {keywordData?.top_keywords?.from_agents &&
                  Object.entries(keywordData.top_keywords.from_agents).slice(0, 10).map(([word, count]) => (
                    <div key={word} className="flex justify-between items-center">
                      <span className="text-gray-300">{word}</span>
                      <span className="text-blue-400 font-medium">{count}</span>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Outcomes Tab */}
      {activeTab === 'outcomes' && (
        <div className="space-y-6">
          {/* Conversion Funnel */}
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Conversion Funnel</h3>
            <div className="flex items-center justify-around">
              <FunnelStep 
                label="Dialed" 
                value={outcomeData?.conversion_funnel?.dialed || 0}
                percent={100}
                color="blue"
              />
              <div className="text-gray-500">→</div>
              <FunnelStep 
                label="Answered" 
                value={outcomeData?.conversion_funnel?.answered || 0}
                percent={outcomeData?.conversion_funnel?.answered_rate || 0}
                color="purple"
              />
              <div className="text-gray-500">→</div>
              <FunnelStep 
                label="Completed" 
                value={outcomeData?.conversion_funnel?.completed || 0}
                percent={outcomeData?.conversion_funnel?.completion_rate || 0}
                color="green"
              />
            </div>
          </div>

          {/* Outcome Distribution */}
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Outcome Distribution</h3>
              <div className="space-y-3">
                {outcomeData?.outcome_distribution &&
                  Object.entries(outcomeData.outcome_distribution).map(([outcome, count]) => (
                    <div key={outcome} className="flex justify-between items-center">
                      <span className="text-gray-300 capitalize">{outcome.replace('_', ' ')}</span>
                      <span className="text-white font-medium">{count}</span>
                    </div>
                  ))
                }
              </div>
            </div>

            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Status Distribution</h3>
              <div className="space-y-3">
                {outcomeData?.status_distribution &&
                  Object.entries(outcomeData.status_distribution).map(([status, count]) => (
                    <div key={status} className="flex justify-between items-center">
                      <span className="text-gray-300 capitalize">{status.replace('_', ' ')}</span>
                      <span className="text-white font-medium">{count}</span>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>

          {/* Agent Performance */}
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Agent Performance</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-gray-400 text-sm">
                    <th className="pb-3">Agent</th>
                    <th className="pb-3">Total Calls</th>
                    <th className="pb-3">Completed</th>
                    <th className="pb-3">Avg Duration</th>
                    <th className="pb-3">Transferred</th>
                  </tr>
                </thead>
                <tbody>
                  {outcomeData?.agent_performance &&
                    Object.entries(outcomeData.agent_performance).map(([agentId, stats]) => (
                      <tr key={agentId} className="border-t border-gray-700">
                        <td className="py-3 text-white">{stats.name || agentId.slice(0, 8)}</td>
                        <td className="py-3 text-gray-300">{stats.total_calls}</td>
                        <td className="py-3 text-green-400">{stats.completed}</td>
                        <td className="py-3 text-gray-300">{stats.avg_duration}s</td>
                        <td className="py-3 text-amber-400">{stats.transferred}</td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* AI Insights Tab */}
      {activeTab === 'intelligence' && (
        <div className="space-y-6">
          {!conversationIntelligence ? (
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-12 text-center">
              <Brain className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Data Available</h3>
              <p className="text-gray-400">AI insights will appear here once you have call data to analyze.</p>
            </div>
          ) : (
          <>
          {/* Customer Satisfaction */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/10 rounded-xl border border-green-500/30 p-6">
              <div className="flex items-center gap-3 mb-4">
                <ThumbsUp className="w-8 h-8 text-green-400" />
                <div>
                  <h3 className="text-lg font-semibold text-white">Customer Satisfaction</h3>
                  <p className="text-gray-400 text-sm">Based on sentiment analysis</p>
                </div>
              </div>
              <div className="flex items-end gap-2">
                <span className="text-4xl font-bold text-white">
                  {conversationIntelligence.customer_satisfaction?.average_score || 0}
                </span>
                <span className="text-gray-400 mb-1">/ 5</span>
              </div>
              <p className={`text-sm mt-2 ${
                conversationIntelligence.customer_satisfaction?.satisfaction_level === 'Excellent' ? 'text-green-400' :
                conversationIntelligence.customer_satisfaction?.satisfaction_level === 'Good' ? 'text-blue-400' :
                conversationIntelligence.customer_satisfaction?.satisfaction_level === 'Fair' ? 'text-amber-400' : 'text-red-400'
              }`}>
                {conversationIntelligence.customer_satisfaction?.satisfaction_level || 'N/A'}
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-500/20 to-indigo-500/10 rounded-xl border border-purple-500/30 p-6">
              <div className="flex items-center gap-3 mb-4">
                <Brain className="w-8 h-8 text-purple-400" />
                <div>
                  <h3 className="text-lg font-semibold text-white">Top Intent</h3>
                  <p className="text-gray-400 text-sm">Most common customer intent</p>
                </div>
              </div>
              <p className="text-2xl font-bold text-white capitalize">
                {conversationIntelligence.intent_detection?.top_intent?.replace(/_/g, ' ') || 'No data'}
              </p>
              <p className="text-purple-400 text-sm mt-2">
                {conversationIntelligence.intent_detection?.total_intents_detected || 0} intents detected
              </p>
            </div>

            <div className="bg-gradient-to-br from-red-500/20 to-orange-500/10 rounded-xl border border-red-500/30 p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-8 h-8 text-red-400" />
                <div>
                  <h3 className="text-lg font-semibold text-white">Objection Rate</h3>
                  <p className="text-gray-400 text-sm">Customer objections detected</p>
                </div>
              </div>
              <p className="text-2xl font-bold text-white">
                {conversationIntelligence.objection_tracking?.objection_rate || 0}%
              </p>
              <p className="text-red-400 text-sm mt-2">
                {conversationIntelligence.objection_tracking?.total_objections || 0} total objections
              </p>
            </div>
          </div>

          {/* Intent Trends */}
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Intent Detection Trends</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {conversationIntelligence.intent_detection?.trends &&
                Object.entries(conversationIntelligence.intent_detection.trends).map(([intent, count]) => (
                  <div key={intent} className="bg-gray-700/50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-purple-400">{count}</p>
                    <p className="text-gray-400 text-sm capitalize">{intent.replace(/_/g, ' ')}</p>
                  </div>
                ))
              }
            </div>
          </div>

          {/* Top Objections */}
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Top Objections</h3>
            <div className="space-y-3">
              {conversationIntelligence.objection_tracking?.top_objections &&
                Object.entries(conversationIntelligence.objection_tracking.top_objections).map(([objection, count]) => (
                  <div key={objection} className="flex items-center gap-4">
                    <div className="flex-1 bg-gray-700 rounded-full h-3 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-red-500 to-orange-500"
                        style={{ width: `${Math.min((count / (conversationIntelligence.total_calls_analyzed || 1)) * 100 * 5, 100)}%` }}
                      />
                    </div>
                    <span className="text-gray-400 text-sm w-40 truncate">{objection}</span>
                    <span className="text-white font-medium w-12 text-right">{count}</span>
                  </div>
                ))
              }
            </div>
          </div>
          </>
          )}
        </div>
      )}

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <div className="space-y-6">
          {!performanceInsights ? (
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-12 text-center">
              <Target className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Performance Data</h3>
              <p className="text-gray-400">Performance insights will appear here once you have call data to analyze.</p>
            </div>
          ) : (
          <>
          {/* Best Time Slots */}
          <div className="bg-gradient-to-br from-amber-500/20 to-yellow-500/10 rounded-xl border border-amber-500/30 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Zap className="w-8 h-8 text-amber-400" />
              <div>
                <h3 className="text-lg font-semibold text-white">Best Performing Time</h3>
                <p className="text-gray-400 text-sm">{performanceInsights.best_performing_time_slots?.recommendation}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-4">
              {performanceInsights.best_performing_time_slots?.slots &&
                Object.entries(performanceInsights.best_performing_time_slots.slots).map(([slot, data]) => (
                  <div 
                    key={slot} 
                    className={`rounded-lg p-3 text-center ${
                      slot === performanceInsights.best_performing_time_slots?.best_slot?.name 
                        ? 'bg-amber-500/30 border border-amber-500' 
                        : 'bg-gray-700/50'
                    }`}
                  >
                    <p className="text-white font-medium text-sm capitalize">{slot.replace(/_/g, ' ')}</p>
                    <p className="text-gray-400 text-xs">{data.range}</p>
                    <p className={`text-lg font-bold mt-1 ${data.success_rate > 50 ? 'text-green-400' : 'text-gray-400'}`}>
                      {data.success_rate}%
                    </p>
                    <p className="text-gray-500 text-xs">{data.calls} calls</p>
                  </div>
                ))
              }
            </div>
          </div>

          {/* Conversion by Language */}
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Conversion by Language</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {performanceInsights.conversion_by_language &&
                Object.entries(performanceInsights.conversion_by_language).map(([lang, data]) => (
                  <div key={lang} className="bg-gray-700/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">{lang}</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        data.success_rate >= 50 ? 'bg-green-500/20 text-green-400' : 'bg-gray-600 text-gray-400'
                      }`}>
                        {data.success_rate}% success
                      </span>
                    </div>
                    <div className="text-gray-400 text-sm space-y-1">
                      <p>Total Calls: {data.calls}</p>
                      <p>Successful: {data.success}</p>
                      <p>Avg Duration: {data.avg_duration_seconds}s</p>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>

          {/* Agent Script Effectiveness */}
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Agent Script Effectiveness</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-gray-400 text-sm">
                    <th className="pb-3">Agent</th>
                    <th className="pb-3">Calls</th>
                    <th className="pb-3">Success Rate</th>
                    <th className="pb-3">Avg Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {performanceInsights.agent_script_effectiveness &&
                    Object.entries(performanceInsights.agent_script_effectiveness).map(([agentId, stats]) => (
                      <tr key={agentId} className="border-t border-gray-700">
                        <td className="py-3 text-white">{agentId.slice(0, 12)}...</td>
                        <td className="py-3 text-gray-300">{stats.calls}</td>
                        <td className="py-3">
                          <span className={stats.success_rate >= 50 ? 'text-green-400' : 'text-gray-400'}>
                            {stats.success_rate}%
                          </span>
                        </td>
                        <td className="py-3 text-gray-300">{stats.avg_call_duration}s</td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          </div>
          </>
          )}
        </div>
      )}

      {/* Comparison Tab */}
      {activeTab === 'comparison' && (
        <div className="space-y-6">
          {/* Period Comparison */}
          {periodComparison ? (
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">This Week vs Last Week</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <ComparisonCard 
                  title="Total Calls"
                  current={periodComparison.current_period?.metrics?.total_calls || 0}
                  change={periodComparison.comparison?.total_calls_change || 0}
                />
                <ComparisonCard 
                  title="Answer Rate"
                  current={`${periodComparison.current_period?.metrics?.answer_rate || 0}%`}
                  change={periodComparison.comparison?.answer_rate_change || 0}
                />
                <ComparisonCard 
                  title="Completion Rate"
                  current={`${periodComparison.current_period?.metrics?.completion_rate || 0}%`}
                  change={periodComparison.comparison?.completion_rate_change || 0}
                />
                <ComparisonCard 
                  title="Avg Duration"
                  current={`${periodComparison.current_period?.metrics?.avg_duration || 0}s`}
                  change={periodComparison.comparison?.avg_duration_change || 0}
                />
              </div>
              <div className="mt-4 flex items-center gap-2">
                <span className="text-gray-400">Overall Trend:</span>
                <span className={`px-3 py-1 rounded-full text-sm ${
                  periodComparison.comparison?.trend === 'improving' ? 'bg-green-500/20 text-green-400' :
                  periodComparison.comparison?.trend === 'declining' ? 'bg-red-500/20 text-red-400' :
                  'bg-gray-600 text-gray-400'
                }`}>
                  {periodComparison.comparison?.trend?.charAt(0).toUpperCase() + periodComparison.comparison?.trend?.slice(1) || 'Stable'}
                </span>
              </div>
            </div>
          ) : (
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-12 text-center">
              <GitCompare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Comparison Data</h3>
              <p className="text-gray-400">Period comparison will appear here once you have call data to analyze.</p>
            </div>
          )}

          {/* Agent Comparison */}
          {agentComparison && agentComparison.rankings?.length > 0 ? (
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Agent Rankings</h3>
                {agentComparison.top_performer && (
                  <div className="flex items-center gap-2 bg-amber-500/20 text-amber-400 px-3 py-1 rounded-full text-sm">
                    <Award className="w-4 h-4" />
                    Top: {agentComparison.top_performer.name}
                  </div>
                )}
              </div>
              <div className="space-y-3">
                {agentComparison.rankings?.map((agent, idx) => (
                  <div key={agent.agent_id} className="flex items-center gap-4 bg-gray-700/50 rounded-lg p-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      idx === 0 ? 'bg-amber-500 text-white' :
                      idx === 1 ? 'bg-gray-400 text-white' :
                      idx === 2 ? 'bg-orange-700 text-white' :
                      'bg-gray-600 text-gray-300'
                    }`}>
                      {idx + 1}
                    </span>
                    <div className="flex-1">
                      <p className="text-white font-medium">{agent.name}</p>
                      <p className="text-gray-400 text-sm">Score: {agent.score.toFixed(1)}</p>
                    </div>
                    <div className="w-32 bg-gray-700 rounded-full h-2 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-500 to-blue-500"
                        style={{ width: `${agent.score}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : !periodComparison && (
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-12 text-center">
              <Award className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Agent Rankings</h3>
              <p className="text-gray-400">Agent rankings will appear here once you have call data to analyze.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper Components
const ComparisonCard = ({ title, current, change }) => (
  <div className="bg-gray-700/50 rounded-lg p-4">
    <p className="text-gray-400 text-sm mb-1">{title}</p>
    <p className="text-xl font-bold text-white">{current}</p>
    <div className={`flex items-center gap-1 text-sm mt-1 ${
      change > 0 ? 'text-green-400' : change < 0 ? 'text-red-400' : 'text-gray-400'
    }`}>
      {change > 0 ? <ArrowUp className="w-3 h-3" /> : change < 0 ? <ArrowDown className="w-3 h-3" /> : null}
      <span>{Math.abs(change).toFixed(1)}%</span>
    </div>
  </div>
);

const MetricCard = ({ title, value, subtitle, icon: Icon, color }) => {
  const colors = {
    blue: 'bg-blue-500/20 text-blue-400',
    green: 'bg-green-500/20 text-green-400',
    purple: 'bg-purple-500/20 text-purple-400',
    amber: 'bg-amber-500/20 text-amber-400',
    red: 'bg-red-500/20 text-red-400'
  };

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-gray-400 text-sm">{title}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
    </div>
  );
};

const FunnelStep = ({ label, value, percent, color }) => {
  const colors = {
    blue: 'bg-blue-500',
    purple: 'bg-purple-500',
    green: 'bg-green-500'
  };

  return (
    <div className="text-center">
      <div className={`w-24 h-24 rounded-full ${colors[color]} flex items-center justify-center mx-auto mb-2`}>
        <span className="text-white text-xl font-bold">{value}</span>
      </div>
      <p className="text-white font-medium">{label}</p>
      <p className="text-gray-400 text-sm">{percent.toFixed(1)}%</p>
    </div>
  );
};

export default VoiceAnalyticsDashboard;
